import { useId } from "react";
import { TextField } from "@/components/TextField";
import { PhoneField } from "@/components/PhoneField";
import { FieldMarker } from "@/components/FieldMarker";
import { FIELD_ERROR } from "@/components/fieldStyles";
import { TOUCH_TARGET } from "@/components/buttonStyles";
import {
  RepeatableRows,
  SectionCard,
  type WizardFieldErrors,
  type WizardStepContext,
} from "@/base";
import {
  CONTACT_ROLE_OPTIONS,
  contactCandidates,
  contactFromAccount,
  contactFromPerson,
  newContact,
  type ContactRole,
  type WcContact,
} from "@/lib/submissions/wc/contacts";
import type { WcDraft } from "@/lib/submissions/wc/draft";
import { AddFromRecord } from "@/components/submissions/wc/AddFromRecord";
import { newRowKey } from "@/lib/submissions/wc/rowKey";
import { TWO_COLUMNS } from "@/components/submissions/wc/grids";

// The People step's other contacts (BRK-S337; on People since BRK-S391): who
// else the carrier should call about inspections, accounting records or
// claims, besides the owners, who tick theirs on their own row. They are the
// submission's own people (PMS-S740), in the same list as the owners and
// officers, so someone already on the submission is made a contact rather than
// entered twice, and an account person is copied in, never edited (Nav,
// 2026-10-08). Optional. Its own white section card (BRK-S349). The People
// step reads the people first, and says where each row lands in the list it
// writes (`index`).

export function ContactsSection({
  draft,
  setDraft,
  errors,
  busy,
  index,
}: Pick<
  WizardStepContext<WcDraft>,
  "draft" | "setDraft" | "errors" | "busy"
> & {
  index: Readonly<Record<string, number>>;
}) {
  const personnel = draft.personnel ?? [];
  const rows = draft.contacts;
  const setRows = (contacts: WcContact[]) =>
    setDraft((d) => ({ ...d, contacts }));
  const { onSubmission, onAccount } = contactCandidates(
    draft.account?.personnel ?? [],
    personnel,
    rows,
  );
  const candidates = [
    ...onSubmission.map((p) => ({
      key: p.id,
      label: `${p.firstName} ${p.lastName} (on this submission)`,
      add: () => contactFromPerson(p, p.id),
    })),
    ...onAccount.map((p) => ({
      key: p.id,
      label: `${p.firstName} ${p.lastName}`,
      add: () => contactFromAccount(p, newRowKey()),
    })),
  ];

  return (
    <SectionCard title="Other contacts" titleAs="h3">
      <div className="flex flex-col gap-3">
        <p className="text-body text-brk-dim">
          Who else the carrier should call about inspections, accounting records
          or claims. Optional.
        </p>
        <AddFromRecord
          label="Add someone already on file"
          placeholder="Choose a person"
          choices={candidates}
          onAdd={(key) => {
            const c = candidates.find((choice) => choice.key === key);
            if (c) setRows([...rows, c.add()]);
          }}
          disabled={busy}
        />
        <RepeatableRows
          label="Contacts"
          rows={rows}
          onChange={setRows}
          newRow={() => newContact(newRowKey())}
          rowKey={(row) => row.key}
          itemLabel={(_row, i) => `Contact ${i + 1}`}
          addLabel="Add a contact"
          headingLevel={4}
          renderRow={(row, update) => (
            <ContactFields
              row={row}
              update={update}
              errors={errors}
              at={(field) => `personnel.${index[row.key]}.${field}`}
              busy={busy}
            />
          )}
        />
      </div>
    </SectionCard>
  );
}

function ContactFields({
  row,
  update,
  errors,
  at,
  busy,
}: {
  row: WcContact;
  update: (next: WcContact) => void;
  errors: WizardFieldErrors;
  /** Where this row's fields are keyed, as PMS keys its refusals. */
  at: (field: string) => string;
  busy: boolean;
}) {
  const set = (patch: Partial<WcContact>) => update({ ...row, ...patch });
  // PMS names a phone by its list path (`personnel.N.phones.0.number`).
  const phoneError =
    errors[at("phones")] ??
    Object.entries(errors).find(([path]) =>
      path.startsWith(`${at("phones")}.`),
    )?.[1];
  return (
    <div className={TWO_COLUMNS}>
      <TextField
        label="First name"
        value={row.firstName}
        onChange={(e) => set({ firstName: e.target.value })}
        error={errors[at("firstName")]}
        disabled={busy}
        required
      />
      <TextField
        label="Last name"
        value={row.lastName}
        onChange={(e) => set({ lastName: e.target.value })}
        error={errors[at("lastName")]}
        disabled={busy}
        required
      />
      <TextField
        label="Email"
        type="email"
        value={row.email}
        onChange={(e) => set({ email: e.target.value })}
        error={errors[at("email")]}
        disabled={busy}
        optional
      />
      <PhoneField
        label="Phone"
        value={row.phone}
        onChange={(phone) => set({ phone })}
        error={phoneError}
        optional
      />
      <div className="sm:col-span-2">
        <ContactFor
          legend="Contact for"
          required
          value={row.contactRoles}
          onChange={(contactRoles) => set({ contactRoles })}
          error={errors[at("roles")]}
          disabled={busy}
        />
      </div>
    </div>
  );
}

/** What the person is the contact for: ACORD 130's types, a contact's (one
 *  or more) or an owner's (any, BRK-S391). */
export function ContactFor({
  legend,
  required,
  value,
  onChange,
  error,
  disabled,
}: {
  legend: string;
  required?: boolean;
  value: ContactRole[];
  onChange: (roles: ContactRole[]) => void;
  error: string | undefined;
  disabled: boolean;
}) {
  const errorId = useId();
  const toggle = (role: ContactRole) =>
    onChange(
      CONTACT_ROLE_OPTIONS.map((o) => o.value).filter((r) =>
        r === role ? !value.includes(role) : value.includes(r),
      ),
    );
  return (
    <fieldset
      className="flex flex-col gap-1.5"
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? errorId : undefined}
    >
      <legend className="flex items-baseline gap-1.5 text-sm font-medium text-brk-text">
        {legend}
        {required && <FieldMarker required />}
      </legend>
      <div className="flex flex-col gap-1.5 sm:flex-row sm:gap-4">
        {CONTACT_ROLE_OPTIONS.map((o) => (
          <label
            key={o.value}
            className={`flex items-center gap-2 text-sm text-brk-text ${TOUCH_TARGET}`}
          >
            <input
              type="checkbox"
              checked={value.includes(o.value)}
              onChange={() => toggle(o.value)}
              disabled={disabled}
            />
            {o.label}
          </label>
        ))}
      </div>
      {error && (
        <p id={errorId} className={FIELD_ERROR}>
          {error}
        </p>
      )}
    </fieldset>
  );
}
